
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FiShoppingCart, 
  FiUser, 
  FiLogOut, 
  FiMenu, 
  FiX, 
  FiHome,
  FiGrid,
  FiLock,
  FiUserPlus,
  FiStar,
  FiShoppingBag
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Navbar = ({ showAdminControls = false, onBackToDashboard }) => {
  const { user, isAuthenticated, logout, isAdmin, isSubAdmin, isSupAdmin, isMainAdmin } = useAuth();
  const { openCart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    updateCartCount();
    
    const handleCartUpdate = () => {
      updateCartCount();
    };
    
    window.addEventListener('cart-updated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, [isAuthenticated, user]);

  const updateCartCount = () => {
    if (isAuthenticated) {
      fetchCartCountFromBackend();
    } else {
      const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      const count = guestCart.reduce((total, item) => total + (item.quantity || 1), 0);
      setCartCount(count);
    }
  };

  const fetchCartCountFromBackend = async () => {
    try {
      const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      const count = guestCart.reduce((total, item) => total + (item.quantity || 1), 0);
      setCartCount(count);
    } catch (error) {
      console.error('Erreur récupération panier:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setMobileMenuOpen(false);
      if (location.pathname.includes('/dashboard') || location.pathname.includes('/admin')) {
        navigate('/');
      }
    } catch (error) {
      console.error('Erreur déconnexion:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const isOnAdminPage = location.pathname.includes('/dashboard') || 
                        location.pathname.includes('/admin');

  const canAccessDashboard = isAuthenticated && (isMainAdmin || isSubAdmin || isSupAdmin);


  const handleCartClick = () => {
    openCart();
  };

  // Fonction pour obtenir le nom du rôle à afficher
  const getRoleDisplayName = () => {
    const roleName = user?.role?.name;
    
    if (!roleName) return '';
    
    switch(roleName) {
      case 'admin': return 'Administrateur';
      case 'sous_admin': return 'Sous-admin';
      case 'user': return 'Client';
      default: return roleName.charAt(0).toUpperCase() + roleName.slice(1);
    }
  };

  // Fonction pour obtenir la couleur du badge selon le rôle (adaptée au nouveau thème)
  const getRoleBadgeColor = () => {
    const roleName = user?.role?.name;
    
    if (!roleName) return 'bg-gray-200 text-gray-800';
    
    switch(roleName) {
      case 'admin': return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'sous_admin': return 'bg-gray-100 text-gray-800 border border-gray-200';
      case 'user': return 'bg-gray-50 text-gray-700 border border-gray-200';
      default: return 'bg-gray-200 text-gray-800';
    }
  };

  // Nouvelles couleurs de la charte graphique (jaune clair et gris legerement clair)
  const primaryColor = 'text-yellow-400'; 
  const primaryBg = 'bg-yellow-400'; 
  const primaryHover = 'hover:bg-yellow-400'; 
  const primaryLight = 'bg-yellow-400'; 
  const primaryBorder = 'border-yellow-400'; 
  
  const secondaryColor = 'text-gray-400'; 
  const secondaryBg = 'bg-gray-400'; 
  const secondaryHover = 'hover:bg-gray-400'; 
  const secondaryLight = 'bg-gray-400'; 
  const secondaryBorder = 'border-gray-400';

  return (
    <nav className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo et Navigation principale */}
          <div className="flex items-center space-x-6">
            {/* Logo avec nouveau design */}
            <Link to="/" className="flex items-center space-x-2 group" onClick={closeMobileMenu}>
              <img 
                src="/logo192.png" 
                alt="Stelonne Market" 
                className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div className="flex flex-col">
                <span className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                  <span className={primaryColor}>Stelonne</span>
                  <span className="text-gray-600">Market</span>
                </span>
                <span className="text-xs text-gray-500 font-medium tracking-wide">
                  Decouvrez - Achetez - Profitez
                </span>
              </div>
            </Link>

            {/* Navigation Desktop - Liens principaux */}
            <div className="hidden md:flex items-center space-x-4">
              <Link 
                to="/" 
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  location.pathname === '/' 
                    ? `${primaryBg} text-white shadow-md` 
                    : `${secondaryColor} hover:${secondaryLight} hover:text-gray-900`
                }`}
              >
                Accueil
              </Link>
              
              {/* Dashboard pour admin/sous-admin */}
              {canAccessDashboard && !isOnAdminPage && (
                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 
                    ${secondaryColor} hover:${secondaryLight} hover:text-gray-900 border border-transparent hover:border-gray-300`}
                >
                  <FiGrid className="text-lg" />
                  <span>Dashboard</span>
                </Link>
              )}
            </div>
          </div>

          {/* Actions Desktop (droite) */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Panier avec nouveau design */}
            <button
              onClick={handleCartClick}
              className={`relative p-2 ${secondaryColor} hover:${primaryColor} transition-all duration-200 group`}
            >
              <div className="relative">
                <FiShoppingCart className="text-2xl" />
                {cartCount > 0 && (
                  <span className={`absolute -top-2 -right-2 ${primaryBg} text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-md animate-pulse`}>
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </div>
              {/* Tooltip */}
              <div className="absolute top-full right-0 mt-2 w-48 bg-white shadow-xl rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 border border-gray-200">
                <div className="text-sm">
                  <p className="font-semibold text-gray-900">Mon panier</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {cartCount} article{cartCount !== 1 ? 's' : ''}
                  </p>
                  <div className="mt-2">
                    <button 
                      onClick={handleCartClick}
                      className={`w-full ${primaryBg} text-white py-1.5 rounded-lg text-sm font-medium hover:${primaryHover} transition-colors`}
                    >
                      Voir le panier
                    </button>
                  </div>
                </div>
              </div>
            </button>

            {/* Section utilisateur */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                {/* Nom d'utilisateur avec nouveau design */}
                <div className={`flex items-center space-x-2 px-4 py-2 ${secondaryLight} rounded-xl border ${secondaryBorder}`}>
                  <div className={`p-2 ${primaryLight} rounded-lg`}>
                    <FiUser className={`${primaryColor}`} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-900 text-sm">
                      {user?.name || user?.email?.split('@')[0] || 'Utilisateur'}
                    </span>
                    {user?.role?.name && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor()} font-medium`}>
                        {getRoleDisplayName()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Bouton Dashboard si on est sur une page admin */}
                {isOnAdminPage && onBackToDashboard && canAccessDashboard && (
                  <button
                    onClick={onBackToDashboard}
                    className={`flex items-center space-x-2 px-4 py-2 ${primaryBg} text-white rounded-lg hover:${primaryHover} transition-colors shadow-md hover:shadow-lg`}
                    title="Retour au tableau de bord"
                  >
                    <FiGrid className="text-lg" />
                    <span>Dashboard</span>
                  </button>
                )}

                {/* Déconnexion avec nouveau design */}
                <button
                  onClick={handleLogout}
                  className={`flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 rounded-lg transition-all duration-200`}
                  title="Déconnexion"
                >
                  <FiLogOut className="text-lg" />
                  <span className="text-sm font-medium">Déconnexion</span>
                </button>
              </div>
            ) : (
              /* Section non connecté - Nouveau design */
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className={`flex items-center space-x-2 px-4 py-2 border ${secondaryBorder} text-gray-700 hover:${secondaryLight} hover:border-gray-400 rounded-lg transition-all duration-200`}
                >
                  <FiLock className="text-lg" />
                  <span className="font-medium">Connexion</span>
                </Link>
                <Link
                  to="/register"
                  className={`flex items-center space-x-2 px-4 py-2 ${primaryBg} text-white rounded-lg hover:${primaryHover} transition-colors shadow-md hover:shadow-lg`}
                >
                  <FiUserPlus className="text-lg" />
                  <span className="font-medium">Inscription</span>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button & Cart */}
          <div className="md:hidden flex items-center space-x-3">
            {/* Panier Mobile */}
            <button
              onClick={handleCartClick}
              className="relative p-2 text-gray-700 hover:text-yellow-600 transition-colors"
            >
              <div className="relative">
                <FiShoppingCart className="text-2xl" />
                {cartCount > 0 && (
                  <span className={`absolute -top-2 -right-2 ${primaryBg} text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-md`}>
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </div>
            </button>

            {/* Hamburger Menu avec nouveau design */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 ${secondaryColor} hover:${secondaryLight} rounded-lg transition-colors border ${mobileMenuOpen ? 'border-gray-300' : 'border-transparent'}`}
            >
              {mobileMenuOpen ? <FiX className="text-2xl" /> : <FiMenu className="text-2xl" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu avec nouveau design */}
        <div className={`md:hidden fixed left-0 right-0 bg-white shadow-2xl transition-all duration-300 ease-in-out z-40 border-t border-gray-200 ${
          mobileMenuOpen 
            ? 'top-16 opacity-100 visible max-h-[calc(100vh-4rem)] overflow-y-auto' 
            : 'top-0 opacity-0 invisible max-h-0'
        }`}>
          <div className="px-4 pt-4 pb-6 space-y-2">
            {/* Navigation mobile */}
            <Link
              to="/"
              className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                location.pathname === '/'
                  ? `${primaryBg} text-white shadow-md`
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
              onClick={closeMobileMenu}
            >
              <FiHome className="text-lg" />
              <span className="font-medium">Accueil</span>
            </Link>

            {/* Dashboard mobile pour admin/sous-admin */}
            {canAccessDashboard && !isOnAdminPage && (
              <Link
                to="/dashboard"
                className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-xl transition-all"
                onClick={closeMobileMenu}
              >
                <FiGrid className="text-lg" />
                <span className="font-medium">Dashboard</span>
                <span className={`text-xs px-2 py-1 rounded-full ${getRoleBadgeColor()} font-medium`}>
                  {getRoleDisplayName()}
                </span>
              </Link>
            )}

            {/* Section utilisateur mobile */}
            <div className="border-t border-gray-200 pt-4 mt-2">
              {isAuthenticated ? (
                <>
                  {/* Infos utilisateur avec nouveau design */}
                  <div className="px-4 py-3 bg-gray-50 rounded-xl mb-4 border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 ${primaryLight} rounded-lg`}>
                        <FiUser className={`${primaryColor}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {user?.name || user?.email?.split('@')[0] || 'Utilisateur'}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-gray-600">Rôle :</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor()} font-medium`}>
                            {getRoleDisplayName()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Retour dashboard si sur page admin */}
                  {isOnAdminPage && onBackToDashboard && canAccessDashboard && (
                    <button
                      onClick={() => {
                        onBackToDashboard();
                        closeMobileMenu();
                      }}
                      className={`w-full flex items-center space-x-3 px-4 py-3 ${primaryBg} text-white rounded-xl hover:${primaryHover} transition-colors shadow-md mb-3`}
                    >
                      <FiGrid className="text-lg" />
                      <span className="font-medium">Retour au Dashboard</span>
                    </button>
                  )}

                  {/* Déconnexion mobile avec nouveau design */}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400 rounded-xl transition-all"
                  >
                    <FiLogOut className="text-lg" />
                    <span className="font-medium">Déconnexion</span>
                  </button>
                </>
              ) : (
                /* Section non connecté mobile avec nouveau design */
                <>
                  <Link
                    to="/login"
                    className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400 rounded-xl transition-all mb-3"
                    onClick={closeMobileMenu}
                  >
                    <FiLock className="text-lg" />
                    <span className="font-medium">Connexion</span>
                  </Link>
                  <Link
                    to="/register"
                    className={`w-full flex items-center space-x-3 px-4 py-3 ${primaryBg} text-white rounded-xl hover:${primaryHover} transition-colors shadow-md`}
                    onClick={closeMobileMenu}
                  >
                    <FiUserPlus className="text-lg" />
                    <span className="font-medium">Créer un compte</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;